import { FormControl } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

export const FILE_SIZE_LIMIT: number = 100 * 1024 * 1024;
export const FILES_COUNT_LIMIT: number = 20;
export const SCROLL_NEXT_DISTANCE_PX: number = 400;
export const AUTO_SCROLLING_TIMEOUT_MS: number = 300;
export const MESSAGE_HIGHLIGHT_DURATION_MS: number = 2500;
export const MESSAGE_DELETE_TIMEOUT_MS: number = 10000;
export const TYPING_REQUEST_DEBOUNCE_MS: number = 800;
export const TYPING_INDICATOR_TIMEOUT_MS: number = 3000;
export const VISIBLE_ATTACHMENTS_LIMIT: number = 3;
export const SCROLL_THRESHOLD: number = 80;
export const SEARCH_PAGE_SIZE: number = 30;

export type AttachmentFileType = 'image' | 'video' | 'file';

export type AttachmentFile = {
	file: File;
	loading: boolean;
	key?: string;
	link?: string;
	subscription?: Subscription;
	previewUrl?: string;
	icon: string;
	name: string;
	type: AttachmentFileType;
};

export interface IChatMessage {
	id: number | string;
	user: IChatUser;
	created_at: string;
	updated_at: string;
	is_pinned?: boolean;
	content: string | null;
	deleted?: boolean;
	ai_generated?: boolean;
	read_by?: Array<IChatReadUser>;
	mentioned_users?: Array<IChatUser>;
	reactions?: Array<{ emoji: string, users: Array<number> }>;
	reply_to?: {
		id: number;
		user: IChatUser;
		content: string;
	};
	attachments?: Array<IChatFileAttachment>;
	status?: 'sent' | 'read' | 'failed' | 'pending';
	config?: {
		actions: Array<{ data: { plan_id: number; planogram_id: number }, type: 'open_planogram' }>;
	};
}

export interface IMessageLocalData {
	replyContent?: string;
	replyUser?: IChatUser;
}

export interface INewChatMessage {
	content: string;
	attachments?: Array<IChatMessageAttachmentPayload>;
	mentioned_users?: Array<number>;
	reply_to?: number;
	localData?: IMessageLocalData;
}

export interface IMessageGroup {
	user: IChatUser;
	messages: Array<IChatMessage>;
}

export interface IGroupedChatMessage {
	dateLabel: string;
	groups: Array<IMessageGroup>;
}

export interface IChatMessages {
	next: string;
	previous: string;
	results: Array<IChatMessage>;
	count?: number;
	anchor_message_id?: number;
	anchor_date?: string;
}

export interface IMentionableUsers extends IChatUser {
	plans: Array<{ id: number, name: string }>;
}

export type StoreItem = {
	id: number;
	name: string;
	users: Array<IMentionableUsers>;
};

export type ChatFormGroup = {
	message: FormControl<string>;
	files?: FormControl<Array<AttachmentFile>>;
};

export type ChatFormValue = {
	message: string;
	files?: Array<AttachmentFile>;
};

export type IChatMessageAttachmentPayload = {
	key: string;
	name: string;
	file: string;
};

export type IChatMessageAttachmentUpdate = {
	key: string;
	name: string;
	file: string;
	content_type: string;
	icon: string;
};

export interface IChatUser {
	id: number;
	full_name: string;
	photo: string | null;
	email?: string;
	role?: string;
	shop?: string | null;
}

export interface IChatReadUser extends IChatUser {
	created_at: string;
}

export interface IChatReadMessage {
	message_ids: Array<number>;
	user: IChatReadUser;
}

export interface IChatFileAttachment {
	file: string;
	name: string;
	key: string;
	content_type: string;
	icon?: string;
	previewUrl?: string;
	type?: AttachmentFileType;
	id?: number;
	created_at?: string;
	can_be_deleted?: boolean;
}

export interface IChatTempMessage {
	message: string;
	files?: Array<AttachmentFile>;
	replyMessage?: IChatMessage | null;
	editingMessage?: IChatMessage | null;
}

export interface IOpenChatEvent {
	chatId: number;
	focusMessageId?: number;
}

export interface IChatAttachments {
	next: string;
	previous: string;
	results: Array<IChatAttachment>;
	count: number;
}

export interface IChatAttachment {
	id: number;
	file: string;
	name: string;
	content_type: AllowedExtensions;
	message: number;
	created_at: string;
	user: IChatUser;
	can_be_deleted: boolean;
}

export type MediaType = 'image' | 'video';
export type DisplayMediaItem = IChatAttachment & { type: MediaType };
export type DisplayDocumentItem = IChatAttachment & { icon: string };

export enum AllowedExtensions {
	Jpg = 'jpg',
	Jpeg = 'jpeg',
	Png = 'png',
	Svg = 'svg',
	Webp = 'webp',
	Gif = 'gif',
	Heic = 'heic',
	Pdf = 'pdf',
	Doc = 'doc',
	Docx = 'docx',
	Xls = 'xls',
	Xlsx = 'xlsx',
	Pptx = 'pptx',
	Txt = 'txt',
	Zip = 'zip',
	Rar = 'rar',
	Mp4 = 'mp4',
	Mov = 'mov'
}

export type GroupedChatMessages = Array<IGroupedChatMessage>;

export interface PinnedIndicator {
	index: number;
	active: boolean;
}

export interface SafeHtmlInternal extends SafeHtml {
	changingThisBreaksApplicationSecurity?: string;
}

export interface OpenPlanogramAction {
	type: 'open_planogram';
	data: {
		plan_id: number;
		planogram_id: number;
	};
}

export interface MessageReaction {
	emoji: string;
	users: Array<number>;
}

export interface IChatReactionSocketPayload {
	emoji: string;
	user_id: number;
	message_id: number;
}

export interface ScrollRestoreState {
	oldScrollTop: number;
	oldScrollHeight: number;
}

export interface SearchAttachmentItem {
	name: string;
	file: string;
	content_type: AllowedExtensions | null;
}

export interface SearchItem {
	id: number;
	created_at: string;
	userName: string;
	content: string;
	attachments: Array<SearchAttachmentItem>;
	is_pinned: boolean;
}

export type SearchTabKey = 'messages' | 'pinned' | 'media' | 'documents';

export interface TabSearchState {
	items: Array<SearchItem>;
	activeIndex: number;
	nextMessagesUrl: string | null;
	previousMessagesUrl: string | null;
	failedThumbKeys: Set<string>;
	resultsScrollTop: number;
}
